// UXGenie Utility Functions

/**
 * Format current date/time to UTC string with microseconds format
 * Format: YYYY-MM-DDTHH:MM:SS.ffffff
 */
export const formatUTCDateTime = (date: Date = new Date()): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}000`;
};

/**
 * Safely parse JSON string, returns null on error
 */
export const safeJSONParse = <T = any>(jsonString: string): T | null => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

/**
 * Clean generated code by removing markdown code blocks
 */
export const cleanGeneratedCode = (code: string): string => {
  let cleaned = code.trim();
  cleaned = cleaned.replace(/^```(?:jsx|javascript|tsx|typescript)?\s*\n?/gm, '');
  cleaned = cleaned.replace(/^```\s*\n?/gm, '');
  cleaned = cleaned.replace(/```\s*$/gm, '');
  return cleaned.trim();
};

/**
 * Extract API spec from action data
 */
export const getApiSpec = (action: any): any => {
  if (!action?.attributes) return null;
  
  const apiSpecAttr = action.attributes.find(
    (attr: any) => attr.attr_name === 'api_spec'
  );
  
  return apiSpecAttr?.attr_json || null;
};

/**
 * Safe payload parsing
 */
export const safeParsePayload = (payload: any): any => {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload || {};
};

/**
 * Find templated fields in payload (fields with ${variable} syntax)
 */
export const findTemplatedFields = (obj: any, fields: Record<string, string> = {}): Record<string, string> => {
  for (const key in obj) {
    const value = obj[key];
    if (typeof value === 'string' && value.includes('${')) {
      const match = value.match(/\${([^}]+)}/);
      if (match) {
        fields[match[1]] = key;
      }
    } else if (typeof value === 'object' && value !== null) {
      findTemplatedFields(value, fields);
    }
  }
  return fields;
};

/**
 * Create nested payload recursively by replacing template variables
 */
export const createNestedPayloadRecursive = (template: any, formData: Record<string, any>): any => {
  if (typeof template === 'string') {
    let result = template;
    for (const [key, value] of Object.entries(formData)) {
      result = result.replace(`\${${key}}`, String(value));
    }
    return result;
  }
  
  if (Array.isArray(template)) {
    return template.map(item => createNestedPayloadRecursive(item, formData));
  }
  
  if (typeof template === 'object' && template !== null) {
    const result: any = {};
    for (const [key, value] of Object.entries(template)) {
      result[key] = createNestedPayloadRecursive(value, formData);
    }
    return result;
  }
  
  return template;
};

/**
 * Calculate modal position to keep it within viewport
 */
export const calculateModalPosition = (
  x: number,
  y: number,
  modalWidth: number = 340,
  modalHeight: number = 200
): { left: number; top: number } => {
  return {
    left: Math.min(x, window.innerWidth - modalWidth),
    top: Math.min(y + 10, window.innerHeight - modalHeight),
  };
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format file size in human-readable form
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 */
export const getFileType = (fileName: string): string => {
  return fileName.split('.').pop() || '';
};

/**
 * Base64-encode a UTF-8 string in chunks to avoid call-stack overflow on
 * large payloads (e.g. full HTML documents) and to safely handle non-ASCII
 * characters that `btoa` alone cannot encode.
 */
export const encodeToBase64 = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

/**
 * Decode a base64 string produced by `encodeToBase64` back to UTF-8 text.
 */
export const decodeFromBase64 = (value: string): string => {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/**
 * Check if a string is HTML content (document or fragment), not React JSX.
 */
export const isHtmlContent = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (/<!DOCTYPE\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) return true;

  if (!/<[a-z][a-z0-9]*[\s>/]/i.test(trimmed)) return false;

  const hasHtmlClass = /\bclass\s*=/i.test(trimmed);
  const hasJsxClass = /\bclassName\s*=/i.test(trimmed);
  if (hasHtmlClass && !hasJsxClass) return true;

  return /<(html|head|body|style|script|title|meta|link)\b/i.test(trimmed);
};

const isHtmlDocument = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return (
    /<!DOCTYPE\s+html/i.test(trimmed) ||
    /^<html[\s>]/i.test(trimmed) ||
    (/<head[\s>]/i.test(trimmed) && /<body[\s>]/i.test(trimmed))
  );
};

export interface WorkflowHtmlValidationResult {
  isValid: boolean;
  html: string | null;
  isPlainText?: boolean;
  error?: string;
}

const unescapeWorkflowHtml = (html: string): string => {
  if (html.includes(String.raw`\n`) && !html.includes('\n')) {
    return html
      .split(String.raw`\n`).join('\n')
      .split(String.raw`\t`).join('\t')
      .split(String.raw`\"`).join('"')
      .split(String.raw`\'`).join("'");
  }
  return html;
};

const normalizeWorkflowHtml = (html: string): string => {
  let normalized = unescapeWorkflowHtml(html.trim());
  if (isHtmlDocument(normalized)) return normalized;

  if (isHtmlContent(normalized)) {
    const hasHeadContent = /<head[\s>]/i.test(normalized) || /<(title|style|meta|link)\b/i.test(normalized);
    if (hasHeadContent && !/<body[\s>]/i.test(normalized)) {
      return `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="UTF-8">\n<meta name="viewport" content="width=device-width, initial-scale=1.0">\n</head>\n<body>\n${normalized}\n</body>\n</html>`;
    }
    if (!/<html[\s>]/i.test(normalized)) {
      return `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="UTF-8">\n<meta name="viewport" content="width=device-width, initial-scale=1.0">\n</head>\n<body>\n${normalized}\n</body>\n</html>`;
    }
  }

  return normalized;
};

export const validateWorkflowHtml = (
  html: string,
  maxSizeInMB = 5
): WorkflowHtmlValidationResult => {
  const normalized = normalizeWorkflowHtml(html);

  if (!isHtmlContent(normalized)) {
    return {
      isValid: false,
      html: null,
      isPlainText: true,
      error: 'Content is not valid HTML',
    };
  }

  const sizeInBytes = new Blob([normalized]).size;
  const sizeInMB = sizeInBytes / (1024 * 1024);
  if (sizeInMB > maxSizeInMB) {
    return {
      isValid: false,
      html: null,
      error: `HTML content is too large (${sizeInMB.toFixed(2)}MB). Maximum allowed is ${maxSizeInMB}MB.`,
    };
  }

  // Allow window.parent for postMessage / iframe checks.
  // Block only DOM/navigation escapes into the parent frame.
  if (
    /\bparent\.document\b|\btop\.location\b|\bparent\.location\b|\bwindow\.parent\.(document|location)\b/i.test(
      normalized
    )
  ) {
    return {
      isValid: false,
      html: null,
      error: 'HTML contains disallowed parent-frame access patterns',
    };
  }

  return { isValid: true, html: normalized };
};

/**
 * Extract html from workflow outputs (e.g. data.outputs.html).
 */
export const extractWorkflowHtmlOutput = (obj: unknown): string | null => {
  if (!obj || typeof obj !== 'object') return null;
  const record = obj as Record<string, unknown>;

  if (record.outputs && typeof record.outputs === 'object') {
    const outputs = record.outputs as Record<string, unknown>;
    if (typeof outputs.html === 'string' && outputs.html.trim()) {
      return outputs.html.trim();
    }
  }

  if (typeof record.html === 'string' && record.html.trim()) {
    return record.html.trim();
  }

  return null;
};

/**
 * Check if a string looks like JSX/template code
 */
export const isJSXCode = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const stripped = cleanGeneratedCode(value);
  if (isHtmlContent(stripped)) return false;
  return (
    (stripped.includes('className=') && stripped.includes('<')) ||
    stripped.startsWith('<section') ||
    stripped.startsWith('<article') ||
    stripped.startsWith('<main') ||
    (stripped.startsWith('<div') && stripped.includes('className'))
  );
};

/**
 * Recursively search for JSX code in any object structure
 */
export const findJSXCodeInObject = (obj: unknown, depth = 0): string | null => {
  if (depth > 10) return null;
  if (typeof obj === 'string' && isJSXCode(obj)) return obj;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findJSXCodeInObject(item, depth + 1);
      if (found) return found;
    }
  }

  if (obj && typeof obj === 'object') {
    const record = obj as Record<string, unknown>;
    const priorityKeys = new Set([
      'result',
      'code',
      'output',
      'generated_code',
      'template',
      'jsx',
      'arg1',
      'text',
    ]);

    for (const key of priorityKeys) {
      if (key in record) {
        const found = findJSXCodeInObject(record[key], depth + 1);
        if (found) return found;
      }
    }

    for (const key of Object.keys(record)) {
      if (!priorityKeys.has(key)) {
        const found = findJSXCodeInObject(record[key], depth + 1);
        if (found) return found;
      }
    }
  }

  return null;
};

/**
 * Extract JSON object/array from a user message, returning json and remaining instructions
 */
export const extractJsonFromMessage = (text: string): { json: string | null; instructions: string } => {
  const trimmedText = text.trim();

  if ((trimmedText.startsWith('{') && trimmedText.endsWith('}')) ||
    (trimmedText.startsWith('[') && trimmedText.endsWith(']'))) {
    try {
      JSON.parse(trimmedText);
      return { json: trimmedText, instructions: 'Generate a comprehensive viewer for this data.' };
    } catch { /* not valid JSON */ }
  }

  if (trimmedText.startsWith('{') || trimmedText.startsWith('[')) {
    const openBracket = trimmedText[0];
    const closeBracket = openBracket === '{' ? '}' : ']';
    let bracketCount = 0;
    let jsonEndIndex = -1;

    for (let i = 0; i < trimmedText.length; i++) {
      if (trimmedText[i] === openBracket) bracketCount++;
      if (trimmedText[i] === closeBracket) bracketCount--;
      if (bracketCount === 0) { jsonEndIndex = i; break; }
    }

    if (jsonEndIndex > 0) {
      const jsonPart = trimmedText.substring(0, jsonEndIndex + 1);
      const remainingText = trimmedText.substring(jsonEndIndex + 1).trim();
      try {
        const parsed = JSON.parse(jsonPart);
        if (typeof parsed === 'object' && parsed !== null) {
          return { json: jsonPart, instructions: remainingText || 'Generate a template for this data' };
        }
      } catch { /* not valid JSON */ }
    }
  }

  return { json: null, instructions: text };
};

/**
 * Converts a template name into a URL-safe slug.
 * "Product Registration" → "product-registration"
 */
export const slugifyTemplateName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

/**
 * Builds the PAL shareable link for a UXGenie template.
 *
 * URL shape: /ux-genie/{slug}/{usageId}
 * - localhost / app / staging  → https://pal.circuitry.ai/ux-genie/...
 * - org tenant                 → https://{org}.pal.circuitry.ai/ux-genie/...
 *
 * output_type is not included — it's already resolved from the usage record itself.
 *
 * @param templateName - display name used to build the slug
 * @param usageId      - aiworker_usage_id
 */
export const getPalUxGenieUrl = (
  templateName: string,
  usageId: string
): string => {
  const slug = slugifyTemplateName(templateName) || 'template';

  if (typeof window === 'undefined') {
    return `https://pal.circuitry.ai/ux-genie/${slug}/${usageId}`;
  }

  const orgName = window.location.hostname.replace(/\.circuitry\.ai$/, '');
  const isGenericHost =
    orgName.includes('localhost') || orgName === 'app' || orgName.includes('staging');

  return isGenericHost
    ? `https://pal.circuitry.ai/ux-genie/${slug}/${usageId}`
    : `https://${orgName}.pal.circuitry.ai/ux-genie/${slug}/${usageId}`;
};

